import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Flex
} from '@chakra-ui/react';
import { Button, FormFeedback, Input } from '@morressier/alembic';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';

interface NewKeywordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (keywordName: string) => void;
  specialtyName?: string;
  selectedField: string;
  defaultValue: string;
  heading?: string;
  subHeading?: string;
  placeholderText?: string;
}

const MAX_LENGTH = 150;
const allowedCharsRegex = /^[A-Za-z0-9 ]+$/;

type NewKeywordFormValues = {
  keywordName: string;
};

export const NewKeywordModal: React.FC<NewKeywordModalProps> = ({
  isOpen,
  onClose,
  onSave,
  specialtyName,
  selectedField,
  defaultValue,
  heading,
  subHeading,
  placeholderText
}) => {
  const normalizedField = selectedField.trim().toLowerCase();
  const modalHeading =
    heading ??
    (normalizedField.includes('sub-specialty') ||
    normalizedField.includes('subspecialty')
      ? 'Create new Sub-Specialty'
      : normalizedField.includes('scandinavian')
      ? 'Create new Scandinavian keyword'
      : `Create new ${
          selectedField.charAt(0).toUpperCase() + selectedField.slice(1)
        }`);
  const modalSubHeading = subHeading || 'Describe your area of expertise.';
  const modalPlaceholder = placeholderText || 'e.g., Nursing Education';

  const {
    register,
    reset,
    trigger,
    getValues,
    watch,
    formState: { errors }
  } = useForm<NewKeywordFormValues>({
    defaultValues: {
      keywordName: defaultValue || ''
    },
    mode: 'onChange'
  });

  const keywordName = watch('keywordName');

  useEffect(() => {
    if (isOpen) {
      reset({
        keywordName: defaultValue || ''
      });
    }
  }, [defaultValue, isOpen, reset]);

  const handleClose = () => {
    reset({ keywordName: '' });
    onClose();
  };

  const handleSave = async () => {
    const keywordName = getValues('keywordName');

    if (!keywordName?.trim()) {
      reset({ keywordName: '' });

      return;
    }

    const isValid = await trigger('keywordName');

    if (!isValid) {
      return;
    }

    onSave(keywordName.trim());
    handleClose();
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      await handleSave();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent zIndex={1002}>
        <ModalHeader>
          {modalHeading}
          {specialtyName && (
            <Text fontSize="sm" color="gray.500" mt={1}>
              for {specialtyName}
            </Text>
          )}
        </ModalHeader>
        <ModalBody>
          <Flex direction={'column'} gap={2}>
            <FieldLabel label={modalSubHeading} subText="" isRequired={false} />
            <Input
              aria-label={`new-${selectedField}`}
              placeholder={modalPlaceholder}
              autoFocus
              isInvalid={!!errors.keywordName}
              maxLength={MAX_LENGTH}
              pr="12"
              onKeyDown={handleKeyDown}
              {...register('keywordName', {
                validate: value => {
                  if (!value?.trim()) {
                    return true;
                  }

                  if (value.length > MAX_LENGTH) {
                    return `Maximum ${MAX_LENGTH} characters allowed`;
                  }

                  if (!allowedCharsRegex.test(value.trim())) {
                    return `Special characters are not allowed in ${selectedField}`;
                  }

                  return true;
                }
              })}
            />
            {errors.keywordName?.message && (
              <FormFeedback variant="error">
                {errors.keywordName.message}
              </FormFeedback>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} type="button">
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            type="button"
            onClick={handleSave}
            disabled={!keywordName}
          >
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
